import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, X } from 'lucide-react'
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  subDays, 
  subWeeks, 
  subMonths, 
  subYears,
  setMonth,
  setYear,
  startOfYear,
  endOfYear
} from 'date-fns'
import { ar, enUS } from 'date-fns/locale'
import RangeCalendar from './RangeCalendar'
import RangePresetButton from './RangePresetButton'
import DateInput from './DateInput'

interface DateRangePickerProps {
  value?: {
    startDate: Date | string | null
    endDate: Date | string | null
  }
  onChange?: (value: {
    startDate: Date | string | null
    endDate: Date | string | null
  }) => void
  onClear?: () => void
  isRTL?: boolean
  shouldCloseOnSelect?: boolean
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  onClear,
  isRTL = false,
  shouldCloseOnSelect = false,
}) => {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [localStartDate, setLocalStartDate] = useState<Date | null>(null)
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null)
  const [selectingStart, setSelectingStart] = useState(true)

  const locale = i18n.language === 'ar' ? ar : enUS
  const now = new Date()

  const hasDateFilter = value?.startDate || value?.endDate

  // Date range presets
  const presets = useMemo(() => {
    return {
      today: {
        label: t('common.today') || 'Today',
        value: { start: now, end: now }
      },
      yesterday: {
        label: t('common.yesterday') || 'Yesterday',
        value: { start: subDays(now, 1), end: subDays(now, 1) }
      },
      thisWeek: {
        label: t('common.this_week') || 'This week',
        value: { start: startOfWeek(now, { locale }), end: endOfWeek(now, { locale }) }
      },
      lastWeek: {
        label: t('common.last_week') || 'Last week',
        value: {
          start: startOfWeek(subWeeks(now, 1), { locale }),
          end: endOfWeek(subWeeks(now, 1), { locale })
        }
      },
      thisMonth: {
        label: t('common.this_month') || 'This month',
        value: { start: startOfMonth(now), end: endOfMonth(now) }
      },
      lastMonth: {
        label: t('common.last_month') || 'Last month',
        value: {
          start: startOfMonth(subMonths(now, 1)),
          end: endOfMonth(subMonths(now, 1))
        }
      
   
      },
     
    }
  }, [t, locale, now])

  // Initialize local state from props
  useEffect(() => {
    if (isOpen) {
      const start = value?.startDate 
        ? (typeof value.startDate === 'string' ? new Date(value.startDate) : value.startDate)
        : null
      const end = value?.endDate 
        ? (typeof value.endDate === 'string' ? new Date(value.endDate) : value.endDate)
        : null
      
      setLocalStartDate(start)
      setLocalEndDate(end)
      setSelectingStart(!start)
    }
  }, [isOpen, value])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleDateSelect = (date: Date) => {
    if (selectingStart || !localStartDate) {
      setLocalStartDate(date)
      setLocalEndDate(null)
      setSelectingStart(false)
    } else {
      if (date < localStartDate) {
        setLocalEndDate(localStartDate)
        setLocalStartDate(date)
      } else {
        setLocalEndDate(date)
      }
      setSelectingStart(true)
    }
  }

  const handlePresetSelect = (preset: typeof presets[keyof typeof presets]) => {
    setLocalStartDate(preset.value.start)
    setLocalEndDate(preset.value.end)
    setSelectingStart(true)
    
    if (onChange) {
      onChange({
        startDate: preset.value.start,
        endDate: preset.value.end,
      })
    }
    
    if (shouldCloseOnSelect) {
      setIsOpen(false)
    }
  }

  const handleApply = () => {
    if (onChange && localStartDate && localEndDate) {
      onChange({
        startDate: localStartDate,
        endDate: localEndDate,
      })
    }
    setIsOpen(false)
  }

  const handleCancel = () => {
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLocalStartDate(null)
    setLocalEndDate(null)
    if (onClear) {
      onClear()
    }
    setIsOpen(false)
  }

  const formatDisplayDate = (date: Date | string | null): string => {
    if (!date) return ''
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return format(dateObj, 'dd/MM/yyyy', { locale })
    } catch (e) {
      return ''
    }
  }

  const displayText = () => {
    if (value?.startDate && value?.endDate) {
      const start = formatDisplayDate(value.startDate)
      const end = formatDisplayDate(value.endDate)
      if (start === end) {
        return start
      }
      return `${start} - ${end}`
    } else if (value?.startDate) {
      return formatDisplayDate(value.startDate)
    }
    return t('common.date') || 'التاريخ'
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            h-10 px-3 py-1 bg-white rounded-lg flex items-center gap-2
            ${hasDateFilter ? 'ring-2 ring-flutter-button' : ''}
            flex-row-reverse
            hover:bg-gray-50 transition-colors border border-gray-200
          `}
          aria-label={t('common.date') || 'التاريخ'}
        >
          <span className={`text-sm font-medium font-['IBM Plex Sans Arabic'] text-gray-950 ${isRTL ? 'text-right' : 'text-left'}`}>
            {displayText()}
          </span>
          <Calendar className="w-5 h-5 text-flutter-button flex-shrink-0" />
        </button>
        
        {hasDateFilter && (
          <button
            type="button"
            onClick={handleClear}
            className="h-10 w-10 bg-white rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors border border-gray-200"
            title={t('common.clear') || 'مسح'}
            aria-label={t('common.clear') || 'مسح'}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {isOpen && (
        <>
          {/* Backdrop/Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Modal Container - Centered */}
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            role="dialog"
            aria-modal="true"
            aria-label={t('common.date_range') || 'نطاق التاريخ'}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-[min(95vw,1000px)] max-h-[85vh] overflow-hidden pointer-events-auto"
              style={{ animation: 'modalSlideIn 0.2s ease-out' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-0 lg:flex-row max-h-[85vh]">
                {/* Left Sidebar - Presets */}
                <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 p-3 lg:border-b-0 lg:border-r lg:w-48 lg:max-h-[calc(85vh-140px)] lg:overflow-y-auto">
                  <div className="text-xs font-semibold text-gray-700 font-['IBM Plex Sans Arabic']">
                    {t('common.quick_select') || 'اختيار سريع'}
                  </div>
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
                    {Object.values(presets).map((preset, index) => (
                      <RangePresetButton
                        key={index}
                        label={preset.label}
                        onClick={() => handlePresetSelect(preset)}
                        isActive={
                          localStartDate && localEndDate &&
                          format(localStartDate, 'yyyy-MM-dd')  &&
                          format(localEndDate, 'yyyy-MM-dd') === format(preset.value.end, 'yyyy-MM-dd')
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Main Content - Calendar */}
                <div className="flex flex-col flex-1 min-w-0">
                  {/* Calendar Section */}
                  <div className="overflow-y-auto flex-1">
                    <RangeCalendar
                      startDate={localStartDate}
                      endDate={localEndDate}
                      onDateSelect={handleDateSelect}
                      isRTL={isRTL}
                    />
                  </div>

                  {/* Bottom Section - Inputs and Actions */}
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 border-t border-gray-200 p-4 bg-gray-50">
                    {/* Date Inputs */}
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                      <DateInput
                        value={localStartDate}
                        onChange={(date) => {
                          setLocalStartDate(date)
                          if (date && localEndDate && date > localEndDate) {
                            setLocalEndDate(null)
                          }
                        }}
                        className="w-full sm:w-36"
                      />
                      <div className="text-md text-gray-500 font-['IBM Plex Sans Arabic'] flex-shrink-0">–</div>
                      <DateInput
                        value={localEndDate}
                        onChange={(date) => {
                          if (date && localStartDate && date >= localStartDate) {
                            setLocalEndDate(date)
                          }
                        }}
                        className="w-full sm:w-36"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex w-full sm:w-auto gap-3">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-['IBM Plex Sans Arabic']"
                      >
                        {t('common.cancel') || 'إلغاء'}
                      </button>
                      <button
                        type="button"
                        onClick={handleApply}
                        disabled={!localStartDate || !localEndDate}
                        className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-medium text-white bg-flutter-button rounded-lg hover:bg-opacity-90 transition-colors font-['IBM Plex Sans Arabic'] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                      >
                        {t('common.apply') || 'تطبيق'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default DateRangePicker

